/**
 * PayHOA legal and policy URLs
 */
export const PAYHOA_URLS = {
    /** PayHOA Terms of Service */
    TERMS_OF_SERVICE: 'https://www.payhoa.com/terms',

    /** PayHOA Privacy Policy */
    PRIVACY_POLICY: 'https://www.payhoa.com/privacy',
} as const;

/**
 * Stripe legal agreement and documentation URLs
 */
export const STRIPE_URLS = {
    /** Stripe homepage */
    HOME: 'https://stripe.com/',

    /** Stripe Services Agreement */
    SERVICES_AGREEMENT: 'https://stripe.com/legal/ssa',

    /** Stripe Connected Account Agreement */
    CONNECTED_ACCOUNT_AGREEMENT: 'https://stripe.com/legal/connect-account',

    /** Issuing Bank Terms (Celtic Bank) */
    ISSUING_BANK_TERMS: 'https://stripe.com/legal/celtic-spend-card',

    /** Stripe E-Sign consent policy */
    E_SIGN_POLICY: 'https://stripe.com/legal/e-sign-consent',
} as const;

/**
 * Connect account onboarding status values returned by the API.
 */
export const ONBOARDING_STATUSES = {
    VERIFIED: 'verified',
    PENDING: 'pending',
    INCOMPLETE: 'incomplete',
    RESTRICTED: 'restricted',
    NOT_STARTED: 'not_started',
    ERROR: 'error',
} as const;

/**
 * Stripe Connect embedded component appearance configuration.
 * Centralizes PayHOA brand colors and typography for the Stripe onboarding UI.
 */
export const STRIPE_APPEARANCE_CONFIG = {
    variables: {
        colorPrimary: '#2a6be6',
        colorBackground: '#ffffff',
        colorText: '#1b1e23',
        colorDanger: '#df1b41',
        fontFamily: '"Proxima Nova", system-ui, sans-serif',
        fontSizeBase: '18px',
        borderRadius: '8px',
        spacingUnit: '10px',
    },
} as const;

/**
 * Stripe-provided brand assets bundled in the app.
 */
export const STRIPE_ASSETS = {
    /** Stripe's official "Powered by Stripe" badge (blurple outlined pill, 300x68). */
    POWERED_BY_BADGE: '/assets/images/app/bank-logos/powered-by-stripe-300x68.png',
} as const;

/**
 * User-facing display name of the org's Stripe Treasury financial account.
 *
 * SD-8703 renamed this from "PayHOA Balance" to "Treasury Balance". Kept here so the TypeScript
 * sites that build the label themselves (rather than reading `friendlyName` off the API) share one
 * source of truth. Templates that render the bare name inline stay literal, per the interpolation
 * convention. NOTE: this is the label for the SPENDABLE FUNDS — it is NOT the feature name
 * "Financial Accounts", which is unchanged.
 */
export const TREASURY_ACCOUNT_NAME = 'Treasury Balance';

/**
 * Disclosure statement text for Stripe Treasury dialogs
 */
export const STRIPE_DISCLOSURE_TEXT = {
    /** Standard partner disclosure for Treasury features */
    PARTNER_DISCLOSURE: `PayHOA partners with <a href="${STRIPE_URLS.HOME}" target="_blank" rel="noopener">Stripe Payments Company</a> for money transmission services and account services with funds held at Fifth Third Bank N.A., Member FDIC. PayHOA Visa® Commercial cards are powered by Stripe and issued by Celtic Bank.`,

    /**
     * SD-8703: Stripe-compliance explanation shown on the "Powered by Stripe" badge beside the
     * Treasury Balance heading. Makes explicit that the funds sit with Stripe, not PayHOA.
     * Copy is approved verbatim — do not reword.
     */
    TREASURY_ACCOUNT_EXPLANATION: `Your community's financial account is provided through Stripe Treasury. PayHOA never holds or has access to your funds.`,
} as const;

/**
 * Cardholder status values
 */
export const CARDHOLDER_STATUSES = {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
    LISTED: 'listed',
    BLOCKED: 'blocked',
} as const;

/**
 * Card status values
 */
export const CARD_STATUSES = {
    INACTIVE: 'inactive',
    ACTIVE: 'active',
    CANCELED: 'canceled',
} as const;

/**
 * Maps a card status to an AppStatusChipComponent color.
 */
export function getCardStatusColor(status: string): string {
    switch (status) {
        case CARD_STATUSES.ACTIVE:
            return 'success';
        case CARD_STATUSES.INACTIVE:
            return 'info';
        case 'expired':
            return 'warn';
        case 'replaced':
        case CARD_STATUSES.CANCELED:
            return 'error';
        default:
            return 'default';
    }
}

/**
 * Card type values
 */
export const CARD_TYPES = {
    VIRTUAL: 'virtual',
    PHYSICAL: 'physical',
} as const;

/**
 * Physical card shipping statuses from Stripe
 */
export const SHIPPING_STATUSES = {
    PENDING: 'pending',
    SHIPPED: 'shipped',
    DELIVERED: 'delivered',
    RETURNED: 'returned',
    FAILURE: 'failure',
} as const;

/**
 * Maps a shipping status to an AppStatusChipComponent color.
 */
export function getShippingStatusColor(status: string): string {
    switch (status) {
        case SHIPPING_STATUSES.DELIVERED:
            return 'success';
        case SHIPPING_STATUSES.SHIPPED:
            return 'info';
        case SHIPPING_STATUSES.PENDING:
            return 'default';
        case SHIPPING_STATUSES.RETURNED:
            return 'warn';
        case SHIPPING_STATUSES.FAILURE:
            return 'error';
        default:
            return 'default';
    }
}

/**
 * Maps a shipping status to a user-friendly label.
 */
export function getShippingStatusLabel(status: string): string {
    switch (status) {
        case SHIPPING_STATUSES.PENDING:
            return 'Pending';
        case SHIPPING_STATUSES.SHIPPED:
            return 'Shipped';
        case SHIPPING_STATUSES.DELIVERED:
            return 'Delivered';
        case SHIPPING_STATUSES.RETURNED:
            return 'Returned';
        case SHIPPING_STATUSES.FAILURE:
            return 'Failed';
        default:
            return status;
    }
}

/**
 * Maps a cardholder status to an AppStatusChipComponent color (mirrors the
 * Cardholders list). Pending-activation nuance is caller-specific and not
 * handled here.
 */
export function getCardholderStatusColor(status: string): string {
    switch (status) {
        case CARDHOLDER_STATUSES.ACTIVE:
            return 'success';
        case CARDHOLDER_STATUSES.LISTED:
            return 'warn';
        case CARDHOLDER_STATUSES.BLOCKED:
            return 'error';
        case CARDHOLDER_STATUSES.INACTIVE:
            return 'info';
        default:
            return 'default';
    }
}

/**
 * Maps a cardholder status to a display label (mirrors the Cardholders list).
 */
export function getCardholderStatusLabel(status: string): string {
    switch (status) {
        case CARDHOLDER_STATUSES.ACTIVE:
            return 'Active';
        case CARDHOLDER_STATUSES.LISTED:
            return 'Under Review';
        case CARDHOLDER_STATUSES.BLOCKED:
            return 'Blocked';
        case CARDHOLDER_STATUSES.INACTIVE:
            return 'Inactive';
        default:
            return status;
    }
}

/**
 * Spending limit interval options
 */
export const SPENDING_LIMIT_INTERVALS = {
    PER_AUTHORIZATION: 'per_authorization',
    DAILY: 'daily',
    WEEKLY: 'weekly',
    MONTHLY: 'monthly',
    YEARLY: 'yearly',
    ALL_TIME: 'all_time',
} as const;

/**
 * Human-readable labels for spending limit intervals
 */
export const SPENDING_LIMIT_INTERVAL_LABELS: Record<string, string> = {
    per_authorization: 'Per Transaction',
    daily: 'Per Day',
    weekly: 'Per Week',
    monthly: 'Per Month',
    yearly: 'Per Year',
    all_time: 'All Time',
} as const;

/**
 * Dispute status values
 */
export const DISPUTE_STATUSES = {
    SUBMITTED: 'submitted',
    WON: 'won',
    LOST: 'lost',
    EXPIRED: 'expired',
    UNSUBMITTED: 'unsubmitted',
} as const;

/**
 * Typical Stripe Issuing dispute resolution window, shown wherever we tell a user that funds
 * are held during the investigation. Single source so the range stays consistent across every
 * surface (cardholder recent activity, dashboard, spend-cards table). SD-7533.
 *
 * Value per Stripe docs: "This process typically takes between 30 and 90 days."
 * https://docs.stripe.com/issuing/purchases/disputes — corrects the prior inconsistent and
 * undocumented copy (cardholder/dashboard said 55–120, spend-cards table said 45–90).
 */
export const DISPUTE_RESOLUTION_WINDOW = '30–90 days';

/**
 * Maps a dispute status to an AppStatusChipComponent color.
 */
export function getDisputeStatusColor(status: string): string {
    switch (status) {
        case DISPUTE_STATUSES.SUBMITTED:
            return 'warn';
        case DISPUTE_STATUSES.WON:
            return 'success';
        case DISPUTE_STATUSES.LOST:
            return 'error';
        case DISPUTE_STATUSES.EXPIRED:
            return 'default';
        case DISPUTE_STATUSES.UNSUBMITTED:
            return 'info';
        default:
            return 'default';
    }
}

/**
 * US state options for address forms
 */
export const US_STATES: { value: string; label: string }[] = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
    { value: 'DC', label: 'District of Columbia' },
];
